/**
 * 案例主数据（Layer 2: Master Data）
 *
 * 案例与专家通过 expertId 关联，不内嵌专家信息。
 * 专家信息变更时只需更新 experts.js，无需同步修改案例。
 *
 * 字段标注规范：
 * @source MANUAL      — 人工录入
 * @mutability STATIC|SEMI_DYNAMIC
 * @consistency SSOT   — 单一数据源
 * @permission ADMIN|OPERATOR
 */

export const EXPERT_CASES = {
  'c1': {
    /** @field id @source SYSTEM @mutability STATIC @consistency SSOT @permission SYSTEM */
    id: 'c1',
    /** @field title @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR */
    title: '核心员工离职预警与挽留实战',
    /** @field summary @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR */
    summary: '通过引入"日清机制"与"红线管理"，在3个月内将项目延期率降低了40%。',
    /** @field content @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR Markdown格式完整案例 */
    content: `### 案例背景：业务转型期的"人才地震"

当时公司正处于从传统业务向数字化转型的关键期，研发团队面临着前所未有的压力。核心架构师小王突然提出离职，这不仅意味着技术断层，更可能引发团队的连锁反应。

### 核心挑战：如何在"高压"与"留人"之间取得平衡？

1. **技术依赖度高**：小王掌握着核心系统的底层逻辑，短期内无人可替代。
2. **士气低落**：团队成员普遍感到疲惫，对未来方向存在疑虑。
3. **外部诱惑大**：竞对公司开出了极具吸引力的薪资。

### 战术拆解：手术刀式的挽留方案

**第一步：深度职涯复盘（而非单纯谈钱）**
主管没有第一时间谈加薪，而是发起了一场深度谈话。发现小王离职的真实原因是对技术边缘化的恐惧，而非薪资。

**第二步：重新定义岗位价值**
为小王申请了"首席技术专家"的虚拟头衔，并划拨专项经费支持其进行前沿技术预研，满足其技术成就感。

**第三步：建立"人才防火墙"**
启动人才备份计划，安排两名高潜员工进行技术继承，降低对单一节点的依赖。

### 实战成效

通过这套组合拳，小王最终决定留任，并带领团队攻克了转型期的核心技术难关。团队流失率从 25% 降至 5% 以内。`,
    /** @field background @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR */
    background: '当时正处于业务的关键转型期，团队面临着前所未有的压力...',
    /** @field difficulty @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR */
    difficulty: '管理深水区挑战：如何在保持高压交付的同时，不损耗核心人才的积极性？',
    /** @field expertInsight @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR */
    expertInsight: '执行力差往往不是态度问题，而是目标拆解与反馈机制的断层。管理者的核心价值在于消除信息不对称，并为下属提供即时反馈。',
    /** @field tags @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR */
    tags: ['执行力', '人才挽留', '华为经验'],
    /** @field expertId @source SYSTEM @mutability STATIC @consistency SSOT @permission SYSTEM 关联专家ID */
    expertId: 'e1',
    /** @field expertName @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR 冗余字段，用于列表展示时减少关联查询 */
    expertName: '张建国',
    /** @field videoUrl @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR */
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    /** @field audioUrl @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR */
    audioUrl: 'https://www.w3schools.com/html/horse.mp3',
    /** @field publishStatus @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR */
    publishStatus: 'published',
    /** @field createdAt @source SYSTEM @mutability STATIC @consistency SSOT @permission SYSTEM */
    createdAt: 1705315200000,
    /** @field relatedTopicIds @source MANUAL @mutability SEMI_DYNAMIC @consistency SSOT @permission OPERATOR */
    relatedTopicIds: ['3', '6'],
  }
}

/**
 * 根据案例ID获取关联的专家ID
 * @param {string} caseId
 * @returns {string|null}
 */
export function getExpertIdByCaseId(caseId) {
  return EXPERT_CASES[caseId]?.expertId || null
}

/**
 * 根据专家ID获取其所有案例
 * @param {string} expertId
 * @returns {Array}
 */
export function getCasesByExpertId(expertId) {
  return Object.values(EXPERT_CASES).filter(c => c.expertId === expertId)
}
